import React, { useEffect, useState } from 'react';
import SubjectCard from './SubjectCard';
import styles from '../../styles/PreasignacionMateriasStyles/PreasignacionMaterias.module.css';

// Importar el servicio de ciclos escolares
import { getCycles } from '../../api/ciclosEscolares';

function StudentDetail({ 
  student, 
  onBack, 
  onPreasignar, 
  onCancelar,
  onAnular,
  materiasSeleccionadas = [],
  onMateriaToggle,
  onSelectAll,
  preasignacionesExistentes = [],
  inscripcionActual,
  tetramestre = null,
  yaPreasignado = false,
  cicloActual = null,
  preasignacionHabilitada = true,
  fechaHabilitacion = null,
  cicloSiguiente = null,
  loading = false
}) {
  // Obtener IDs de materias ya preasignadas (convertir a números para comparación consistente)
  const materiasPreasignadas = preasignacionesExistentes.map(p => Number(p.id_materia));
  const seleccionadasNums = materiasSeleccionadas.map(id => Number(id));
  
  // Calcular créditos totales de materias seleccionadas y preasignadas
  const calcularCreditos = () => {
    const todasLasMaterias = [
      ...(student.materiasAsignadas || []),
      ...(student.materiasPendientes || []),
      ...(student.materiasOpcionales || [])
    ];
    
    let totalCreditos = 0;
    seleccionadasNums.forEach(id => {
      const materia = todasLasMaterias.find(m => Number(m.id) === id);
      if (materia && materia.creditos) {
        totalCreditos += Number(materia.creditos);
      }
    });
    
    return totalCreditos;
  };
  
  const creditosSeleccionados = calcularCreditos();
  
  // Función para manejar el botón de preasignar (el padre calcula el conjunto
  // automáticas + opcionales marcadas y reemplaza las preasignaciones previas).
  const handlePreasignarClick = () => {
    console.log('[StudentDetail] handlePreasignarClick:', {
      materiasSeleccionadas: seleccionadasNums,
    });
    onPreasignar();
  };

  // Total de materias que se preasignarán (automáticas + opcionales marcadas).
  const totalAsignar = seleccionadasNums.length;

  // Estado para el período/ciclo actual
  const [periodoActual, setPeriodoActual] = useState(null);
  const [nombreCicloActual, setNombreCicloActual] = useState(null);

  useEffect(() => {
    async function fetchPeriodoActual() {
      try {
        const ciclos = await getCycles(student.institucion_id || undefined);
        console.log('[StudentDetail] ciclos recibidos:', ciclos);
        if (!Array.isArray(ciclos) || ciclos.length === 0) {
          setPeriodoActual(null);
          setNombreCicloActual(null);
          return;
        }
        const hoy = new Date();
        let cicloVigente = ciclos.find(c => {
          const inicio = c.fecha_inicio ? new Date(c.fecha_inicio) : null;
          const fin = c.fecha_fin ? new Date(c.fecha_fin) : null;
          // Log para depuración de fechas
          console.log('[StudentDetail] comparando ciclo:', c.nombre_ciclo, 'inicio:', inicio, 'fin:', fin, 'hoy:', hoy);
          return inicio && fin && hoy >= inicio && hoy <= fin;
        });
        if (!cicloVigente) {
          cicloVigente = ciclos.reduce((acc, curr) => {
            const currInicio = curr.fecha_inicio ? new Date(curr.fecha_inicio) : null;
            if (!currInicio) return acc;
            if (!acc) return curr;
            const accInicio = acc.fecha_inicio ? new Date(acc.fecha_inicio) : null;
            return currInicio > accInicio ? curr : acc;
          }, null);
        }
        setPeriodoActual(cicloVigente ? cicloVigente.numero_periodo : null);
        setNombreCicloActual(cicloVigente ? cicloVigente.nombre_ciclo || cicloVigente.name : null);
        console.log('[StudentDetail] ciclo seleccionado:', cicloVigente);
      } catch (e) {
        setPeriodoActual(null);
        setNombreCicloActual(null);
        console.error('[StudentDetail] error obteniendo ciclo actual:', e);
      }
    }
    fetchPeriodoActual();
  }, [student.institucion_id]);

  return (
    <div className={styles.detailContainer}>
      {/* Back Button */}
      <button className={styles.backButton} onClick={onBack}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 12H5"/>
          <path d="M12 19l-7-7 7-7"/>
        </svg>
        Volver a búsqueda
      </button>

      {/* Student Title */}
      <div className={styles.studentTitle}>
        <h2>Estudiante: {student.nombre}</h2>
      </div>

      <div className={styles.detailLayout}>
        {/* Student Info Card */}
        <div className={styles.studentInfoCard}>
          <div className={styles.studentAvatar}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          
          <div className={styles.studentData}>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Matrícula</span>
              <span className={styles.dataValue}>{student.matricula || '-'}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Ciclo Actual</span>
              <span className={styles.dataValue}>
                {nombreCicloActual
                  ? nombreCicloActual
                  : <span className={styles.noData}>Sin período asignado</span>}
              </span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Sección</span>
              <span className={styles.dataValue}>
                {student.seccion || <span className={styles.noData}>Sin sección</span>}
              </span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Institución</span>
              <span className={styles.dataValue}>{student.institucion || '-'}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Programa</span>
              <span className={styles.dataValue}>{student.programa || '-'}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Campus</span>
              <span className={styles.dataValue}>{student.campus || '-'}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>Ciclo de Ingreso</span>
              <span className={styles.dataValue}>
                {student.cicloEscolar || <span className={styles.noData}>Sin ciclo</span>}
              </span>
            </div>
          </div>
          
          <div className={styles.creditsSection}>
            <div className={styles.creditsLabel}>Créditos seleccionados</div>
            <div className={styles.creditsValue}>{creditosSeleccionados}</div>
          </div>
          
          {/* Estado de inscripción */}
          <div className={styles.inscripcionInfo}>
            {inscripcionActual ? (
              <div className={styles.inscripcionActiva}>
                <span className={styles.inscripcionLabel}>Inscripción activa:</span>
                <span className={styles.inscripcionEstado}>
                  {inscripcionActual.estado_inscripcion || 'INSCRITO'}
                </span>
              </div>
            ) : (
              /* <div className={styles.sinInscripcion}>
                ℹ️ Sin inscripción activa (se guardará sin asociar)
              </div> */
              null
            )}
          </div>
        </div>

        {/* Subjects Grid */}
        <div className={styles.subjectsGrid}>
          {/* Materias Recursadas (automáticas) */}
          <SubjectCard
            title="Materias Recursadas"
            icon="🔁"
            subjects={student.materiasAsignadas || []}
            type="assigned"
            emptyMessage="No hay materias por recursar"
            selectable={true}
            autoLocked={true}
            materiasSeleccionadas={materiasSeleccionadas}
            materiasPreasignadas={materiasPreasignadas}
            onMateriaToggle={onMateriaToggle}
            onSelectAll={onSelectAll}
          />

          {/* Materias Siguientes (automáticas, tetramestre actual) */}
          <SubjectCard
            title={tetramestre ? `Materias Siguientes (Tetramestre ${tetramestre})` : 'Materias Siguientes'}
            icon="📘"
            subjects={student.materiasPendientes || []}
            type="pending"
            emptyMessage="No hay materias del siguiente tetramestre pendientes"
            selectable={true}
            autoLocked={true}
            materiasSeleccionadas={materiasSeleccionadas}
            materiasPreasignadas={materiasPreasignadas}
            onMateriaToggle={onMateriaToggle}
            onSelectAll={onSelectAll}
          />

          {/* Materias Opcionales (selección manual) */}
          <SubjectCard
            title="Materias Opcionales"
            icon="⚙️"
            subjects={student.materiasOpcionales || []}
            type="optional"
            emptyMessage="No hay materias opcionales disponibles"
            selectable={true}
            materiasSeleccionadas={materiasSeleccionadas}
            materiasPreasignadas={materiasPreasignadas}
            onMateriaToggle={onMateriaToggle}
            onSelectAll={onSelectAll}
          />
        </div>
      </div>

      {/* Selection Info */}
      <div className={styles.selectionInfo}>
        <span>Materias a preasignar: {totalAsignar}</span>
        {materiasPreasignadas.length > 0 && (
          <span className={styles.yaPreasignadas}>
            ({materiasPreasignadas.length} ya preasignadas)
          </span>
        )}
        {yaPreasignado && (
          <span className={styles.yaPreasignadas}>
            Ya se realizó la preasignación de este ciclo{cicloActual?.nombre_ciclo ? ` (${cicloActual.nombre_ciclo})` : ''}. Podrás volver a preasignar cuando inicie el siguiente ciclo.
          </span>
        )}
        {!yaPreasignado && !preasignacionHabilitada && (
          <span className={styles.yaPreasignadas}>
            La preasignación aún no está disponible{fechaHabilitacion ? `. Estará habilitada a partir del ${fechaHabilitacion}` : ''} (31 días antes del inicio del siguiente ciclo{cicloSiguiente?.nombre_ciclo ? ` ${cicloSiguiente.nombre_ciclo}` : ''}).
          </span>
        )}
      </div>

      {/* Action Buttons */}
      <div className={styles.actionButtons}>
        <button 
          className={styles.preassignButton} 
          onClick={handlePreasignarClick}
          disabled={loading || totalAsignar === 0 || yaPreasignado || !preasignacionHabilitada}
        >
          {loading ? 'GUARDANDO...' : (yaPreasignado ? 'YA PREASIGNADO' : (!preasignacionHabilitada ? 'NO DISPONIBLE AÚN' : `PREASIGNAR (${totalAsignar})`))}
        </button>
        {yaPreasignado && onAnular && (
          <button
            className={styles.anularButton}
            onClick={onAnular}
            disabled={loading}
          >
            ANULAR PREASIGNACIÓN
          </button>
        )}
        <button className={styles.cancelButton} onClick={onCancelar}>
          CANCELAR
        </button>
      </div>
    </div>
  );
}

export default StudentDetail;
